/**
 * Canvas object colours (SD-2.3, DESIGN.md §4).
 *
 * WHY THESE ARE ABSOLUTE HEX AND NOT THEME TOKENS, which is the opposite of the rule everywhere
 * else in this feature (UX-DR21). These objects sit on PHOTOGRAPHIC IMAGERY. A tenant-variable
 * accent over a satellite photo is not a brand expression, it is a legibility hazard: the same
 * token that reads clearly on a light card can vanish against a dark roof. DESIGN.md specifies
 * them in absolute colour for that reason, exactly as it does the flux ramp.
 *
 * They live in `lib/solar/` rather than beside the canvas component on purpose. The SD-2.2 guard
 * asserts that `components/project/design/` contains no hardcoded hex, and that guard should stay
 * absolute rather than growing an exception: a hex appearing in a design COMPONENT is still a bug,
 * because chrome must follow the tenant theme. This module is the one sanctioned home for the
 * canvas palette, and `canvasPalette.test.ts` pins every value against DESIGN.md §4 itself.
 */

export const PANEL_ACTIVE = Object.freeze({
  fill: '#16202D',
  stroke: '#B6C2CE',
  strokeWidthPx: 0.6,
} as const);

export const PANEL_GHOST = Object.freeze({
  fill: '#FFFFFF',
  fillOpacity: 0.07,
  stroke: '#E7EEF5',
  strokeWidthPx: 0.9,
  strokeDasharray: '3 2',
} as const);

export const ROOF_FACE_SUNLIT = Object.freeze({
  fill: '#8A7E6F',
  stroke: '#EDF2F7',
  strokeOpacity: 0.7,
} as const);

export const ROOF_FACE_SHADED = Object.freeze({
  fill: '#565045',
  stroke: '#EDF2F7',
  strokeOpacity: 0.7,
} as const);

export const RIDGE_LINE = Object.freeze({
  stroke: '#F2F5F8',
  strokeWidthPx: 2,
} as const);

export const SETBACK_ENVELOPE = Object.freeze({
  stroke: '#FFC65C',
  strokeWidthPx: 2.2,
  strokeDasharray: '7 4',
} as const);

export const KEEP_OUT = Object.freeze({
  fill: '#0C0F14',
  fillOpacity: 0.55,
  stroke: '#FFE9B8',
  strokeWidthPx: 1.8,
} as const);

/**
 * SDR-2.6: correcting the faces. Not in DESIGN.md §4, which predates editing; chosen to read on
 * imagery the way the keep-out does. A selected face is amber; a corner shared by several faces is
 * pale amber, so a rep can see before dragging that it will move more than one face.
 */
export const FACE_EDIT = Object.freeze({
  fill: '#FFFFFF',
  stroke: '#FFFFFF',
  selectedFill: '#F59E0B',
  selectedStroke: '#FBBF24',
  corner: '#FFFFFF',
  sharedCorner: '#FDE68A',
  cornerStroke: '#111827',
} as const);
